import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bell, CalendarX, ClockCounterClockwise, ListChecks, CalendarCheck } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const entityLink = (a) => {
  if (a.entity_type === 'client' && a.client_id) return `/clients/${a.client_id}`;
  if (a.entity_type === 'gabinete' && a.gabinete_id) return `/promocao/${a.gabinete_id}`;
  return '/agenda';
};

const SEEN_ALERTS_KEY = 'gresart_seen_alert_ids';

const readSeenIds = () => {
  try {
    const raw = localStorage.getItem(SEEN_ALERTS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch (e) {
    return new Set();
  }
};

const writeSeenIds = (idsSet) => {
  try {
    localStorage.setItem(SEEN_ALERTS_KEY, JSON.stringify([...idsSet]));
  } catch (e) {
    // ignore storage errors
  }
};

export default function AlertsBell() {
  const [alerts, setAlerts] = useState(null);
  const [open, setOpen] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const [currentAlertIds, setCurrentAlertIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/alerts`);
        setAlerts(res.data);

        // Build stable IDs for every alert item so we can tell "already seen" apart from "new".
        const ids = [
          ...(res.data?.no_visit_alerts || []).map(a => `novisit-${a.client_id}`),
          ...(res.data?.old_panel_alerts || []).map(a => `oldpanel-${a.client_id}-${a.product_code}`),
          ...(res.data?.today_agenda_alerts || []).map(a => `agenda-${a.task_id}`),
          ...(res.data?.pending_task_alerts || []).map(a => `task-${a.task_id}`)
        ];
        setCurrentAlertIds(ids);

        // Drop seen-ids that no longer correspond to an active alert (resolved), then
        // count how many of the current alerts were never marked as seen.
        const stored = readSeenIds();
        const prunedSeen = new Set([...stored].filter(id => ids.includes(id)));
        writeSeenIds(prunedSeen);
        setUnseenCount(ids.filter(id => !prunedSeen.has(id)).length);
      } catch (error) {
        console.error("Error loading alerts:", error);
      }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleOpen = () => {
    setOpen(true);
    // Mark every alert currently shown as "seen" so the red badge turns off,
    // and only lights up again once genuinely new alerts show up on a future load.
    const seen = new Set([...readSeenIds(), ...currentAlertIds]);
    writeSeenIds(seen);
    setUnseenCount(0);
  };

  const visitasSections = [
    {
      key: 'no_visit', icon: CalendarX, color: '#f59e0b', title: 'Visitas Pendentes',
      items: (alerts?.no_visit_alerts || []).map(a => ({
        label: a.client_name,
        detail: a.days_since === null ? 'Nunca visitado' : `${a.days_since} dias`,
        link: `/clients/${a.client_id}`
      }))
    },
    {
      key: 'old_panel', icon: ClockCounterClockwise, color: '#dc2626', title: 'Painéis Antigos',
      items: (alerts?.old_panel_alerts || []).map(a => ({
        label: `${a.client_name} · ${a.product_code}`,
        detail: `${Math.floor(a.days_old / 365)} anos`, link: `/clients/${a.client_id}`
      }))
    }
  ];

  const agendaSections = [
    {
      key: 'today_agenda', icon: CalendarCheck, color: '#009392', title: 'Agenda do Dia',
      items: (alerts?.today_agenda_alerts || []).map(a => ({
        label: `${a.entity_name} · ${a.task_type}`,
        detail: a.scheduled_time || 'Sem hora', link: entityLink(a)
      }))
    }
  ];

  const tarefasSections = [
    {
      key: 'pending_tasks', icon: ListChecks, color: '#b91c1c', title: 'Tarefas',
      items: (alerts?.pending_task_alerts || []).map(a => ({
        label: `${a.entity_name} · ${a.task_type}`,
        detail: a.days_overdue === null ? 'Sem data'
          : a.days_overdue > 0 ? `${a.days_overdue} dia(s) em atraso`
          : `Em ${Math.abs(a.days_overdue)} dia(s)`,
        link: entityLink(a)
      }))
    }
  ];

  const sections = [...visitasSections, ...agendaSections, ...tarefasSections];

  const renderSection = (section) => {
    const Icon = section.icon;
    return (
      <div key={section.key} data-testid={`alerts-section-${section.key}`}>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-stone-500 flex items-center gap-1.5 mb-2">
          <Icon size={16} style={{ color: section.color }} />
          {section.title} ({section.items.length})
        </h4>
        <div className="space-y-1">
          {section.items.map((item, idx) => (
            <Link
              key={idx}
              to={item.link}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-2 text-xs px-2.5 py-2 rounded-lg hover:bg-stone-50 border border-stone-100"
              data-testid={`alerts-item-${section.key}-${idx}`}
            >
              <span className="text-stone-700 truncate">{item.label}</span>
              <span className="font-semibold flex-shrink-0" style={{ color: section.color }}>{item.detail}</span>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <Button
        variant="ghost"
        onClick={handleOpen}
        className="relative bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 rounded-full p-2 sm:p-3 h-auto"
        data-testid="btn-alerts-bell"
        aria-label="Alertas"
      >
        <Bell size={20} weight="bold" className="sm:hidden" />
        <Bell size={24} weight="bold" className="hidden sm:block" />
        {unseenCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4.5 min-w-[18px] px-1 flex items-center justify-center" data-testid="alerts-bell-badge">
            {unseenCount > 9 ? '9+' : unseenCount}
          </span>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2">
              <Bell size={20} className="text-[#009392]" /> Alertas Automáticas
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            {sections.every(s => s.items.length === 0) && (
              <p className="text-sm text-stone-400 text-center py-6" data-testid="alerts-empty">Sem alertas por agora.</p>
            )}
            {sections.some(s => s.items.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div data-testid="alerts-column-visitas">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-stone-700 text-center mb-3 pb-2 border-b border-stone-200">
                    Visitas Pendentes
                  </h3>
                  <div className="space-y-5">
                    {visitasSections.filter(s => s.items.length > 0).map(renderSection)}
                    {visitasSections.every(s => s.items.length === 0) && (
                      <p className="text-xs text-stone-400 text-center">Sem alertas de visitas.</p>
                    )}
                  </div>
                </div>
                <div data-testid="alerts-column-agenda">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-stone-700 text-center mb-3 pb-2 border-b border-stone-200">
                    Agenda do Dia
                  </h3>
                  <div className="space-y-5">
                    {agendaSections.filter(s => s.items.length > 0).map(renderSection)}
                    {agendaSections.every(s => s.items.length === 0) && (
                      <p className="text-xs text-stone-400 text-center">Sem agenda para hoje.</p>
                    )}
                  </div>
                </div>
                <div data-testid="alerts-column-tarefas">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-stone-700 text-center mb-3 pb-2 border-b border-stone-200">
                    Tarefas
                  </h3>
                  <div className="space-y-5">
                    {tarefasSections.filter(s => s.items.length > 0).map(renderSection)}
                    {tarefasSections.every(s => s.items.length === 0) && (
                      <p className="text-xs text-stone-400 text-center">Sem tarefas pendentes.</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
